import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiError } from "@/lib/api";
import { postcardDate, postcardDestination, postcardWarnings } from "@/lib/sharingTreePostcard";
import "./SharingTreePostcard.css";

export default function SharingTreePostcard({ year, period, onClose }) {
  const [destination, setDestination] = useState("");
  const [loadError, setLoadError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [fits, setFits] = useState(false);
  const card = useRef(null);
  const target = postcardDestination(destination);
  const warnings = postcardWarnings(year, period, destination);
  const ready = warnings.length === 0 && confirmed && fits;

  useEffect(() => {
    let alive = true;
    api.get("/sharing-tree/postcard-destination").then(({ data }) => {
      if (alive) {
        setDestination(data.portal_url || "");
        setConfirmed(false);
      }
    }).catch((e) => { if (alive) setLoadError(formatApiError(e)); });
    return () => { alive = false; };
  }, []);

  useLayoutEffect(() => {
    function measure() {
      const node = card.current;
      if (!node) return;
      const bounds = node.getBoundingClientRect();
      const inside = Array.from(node.querySelectorAll("*")).every((child) => {
        const rect = child.getBoundingClientRect();
        // Text glyphs can exceed a short line box by a few pixels even with no
        // clipping. Check actual card bounds, not each text element's scrollHeight.
        return rect.right <= bounds.right + 1 && rect.bottom <= bounds.bottom + 1
          && child.scrollWidth <= child.clientWidth + 1;
      });
      setFits(inside && node.scrollHeight <= node.clientHeight + 1 && node.scrollWidth <= node.clientWidth + 1);
    }
    measure();
    const observer = new ResizeObserver(measure);
    if (card.current) observer.observe(card.current);
    window.addEventListener("resize", measure);
    return () => { observer.disconnect(); window.removeEventListener("resize", measure); };
  }, [year, period, destination]);

  useEffect(() => {
    document.body.classList.add("st-postcard-print");
    document.body.classList.toggle("st-postcard-print-blocked", !ready);
    const escape = (event) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", escape);
    return () => {
      document.body.classList.remove("st-postcard-print", "st-postcard-print-blocked");
      document.removeEventListener("keydown", escape);
    };
  }, [ready, onClose]);

  return createPortal(
    <div className="st-postcard-overlay" role="dialog" aria-modal="true" aria-label={`${year} Sharing Tree postcard`} data-testid="postcard-preview">
      <div className="st-postcard-tools">
        <h2>Sharing Tree {year} postcard mailer</h2>
        <p>6 × 4 inches, landscape. Information side only; leave the reverse blank for manual addressing.</p>
        <p>Print or Save as PDF at <strong>actual size / 100%</strong>, select 4 × 6 inch paper, and disable browser headers and footers.</p>
        <label htmlFor="postcard-destination">Public client portal login destination</label>
        <input id="postcard-destination" data-testid="postcard-destination" type="url" value={destination}
          onChange={(e) => { setDestination(e.target.value); setConfirmed(false); }} />
        <p>This starts email-link sign-in/account setup, not staff sign-in or Saturday check-in. If this is a preview address, replace it with the published portal URL.</p>
        {target.url && <a href={target.url} target="_blank" rel="noopener noreferrer">Open destination to verify</a>}
        <label><input type="checkbox" data-testid="postcard-confirm-destination" checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)} disabled={!target.url} />
          {" "}I verified this is our published client portal login page and reviewed the selected year.</label>
      </div>
      {loadError && <div className="st-postcard-warning" role="alert">Could not load the configured portal destination: {loadError}. Enter and verify the published URL above.</div>}
      {warnings.length > 0 && <div className="st-postcard-warning" role="alert" data-testid="postcard-warnings">
        <strong>Not ready to print.</strong><ul>{warnings.map((text) => <li key={text}>{text}</li>)}</ul>
        Configure this year’s dates and help phone in Settings → Sharing Tree. Paper-return instructions are not required.
      </div>}
      {!fits && <div className="st-postcard-warning" role="alert" data-testid="postcard-overflow">Essential content does not fit at a readable size. Shorten the help phone or portal URL before printing.</div>}
      {period?.custom_message && <div className="st-postcard-warning">The optional annual message is omitted to keep the postcard readable. Pickup details follow signup; this card does not promise a household pickup window.</div>}
      <div className="st-postcard-actions">
        <button type="button" onClick={() => { if (ready) window.print(); }} disabled={!ready} data-testid="postcard-print-btn">Print / Save as PDF</button>
        <button type="button" onClick={onClose}>Close preview</button>
      </div>
      <div className="st-postcard-scroll">
        <main className="st-postcard-card" ref={card} data-testid="postcard-card">
          <header className="st-postcard-header">
            <div className="st-postcard-brand">Pullman Child Welfare</div>
            <h1>Sharing Tree {year}</h1>
          </header>
          <div className="st-postcard-content">
            <section className="st-postcard-copy">
              <p>Holiday help: toys for ages 0–9, gift cards for ages 10–18, and food vouchers for enrolled households.</p>
              <div className="st-postcard-dates">
                <div><strong>Signups open:</strong> {postcardDate(period?.open_date) || "Not configured"}</div>
                <div><strong>Sign up by:</strong> {postcardDate(period?.close_date) || "Not configured"}</div>
              </div>
              <ol className="st-postcard-instructions">
                <li>Enter your email at the portal.</li>
                <li>Open the emailed one-time link to sign in or set up your account.</li>
                <li>Choose Sharing Tree and complete signup during the signup period.</li>
              </ol>
              <p>Pickup details will be shared after signup.</p>
              <p className="st-postcard-help"><strong>Need help? Call</strong><br />{period?.mailer_contact_phone?.trim() || "Not configured"}</p>
            </section>
            <section className="st-postcard-qr">
              <h2>Scan to sign up</h2>
              {target.url ? <QRCodeSVG value={target.url} size={144} level="M" marginSize={4} bgColor="#ffffff" fgColor="#000000" title="Client portal login" data-testid="postcard-qr" /> : <p>Public portal URL required</p>}
              <div className="st-postcard-url" data-testid="postcard-fallback-url">{target.url || "Not configured"}</div>
              <p>Email access required.<br />For help, call the number on this card.</p>
            </section>
          </div>
        </main>
      </div>
    </div>, document.body
  );
}